import type { SymDef } from './types';
import { VENDOR_DEFS } from './vendor';
import { FLUID_DEFS, SOLENOID_DEF } from './fluid';

/*
 * Symbol library. Units are mm, drawn IEC 60617 style around a local origin
 * that sits on the first pin. SVG strings are styled by the host (stroke =
 * current ink colour), so they carry no colours of their own.
 */

const P = (d: string, c?: string) => `<path d="${d}"${c ? ` class="${c}"` : ''}/>`;
const R = (x: number, y: number, w: number, h: number) => `<rect x="${x}" y="${y}" width="${w}" height="${h}"/>`;
const C = (x: number, y: number, r: number) => `<circle cx="${x}" cy="${y}" r="${r}"/>`;
const T = (x: number, y: number, s: number, t: string) =>
  `<text x="${x}" y="${y}" font-size="${s}" text-anchor="middle">${t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</text>`;
const p3 = (f: (x: number) => string) => [0, 8, 16].map(f).join('');

/*
 * Contacts in the IEC 60617 base form: vertical, drawn unactuated, the actuator on the left. Make contact S00227:
 * the blade leans up-left; break contact S00229: the upper lead ends in a stub to the right and the blade, leaning
 * up-right, rests on it.
 */
const NO = 'M0 0V5M0 11V16M0 11L-4 4';
const NC = 'M0 0V5H5M0 11V16M0 11L5.6 3.6';
/** Push actuator S00171: a bracket open toward the link ("E" with the link); "]" would be the pull actuator S00169. */
const PUSH = 'M-9 5.5V10.5M-9 5.5H-7.5M-9 10.5H-7.5';
/** Turn actuator S00170: a step, stub top-right and bottom-left, the link from the middle. */
const TURN = 'M-8 5V11M-8 5H-6.5M-8 11H-9.5';
/** Delay qualifier joined to the blade by a double line: on-delay "(" opens toward the contact, off-delay ")" turns its back to it. */
const delay = (bx: (y: number) => number, off: boolean) =>
  P(`M-7 6.9H${bx(6.9)}M-7 7.7H${bx(7.7)}`) + (off ? P('M-9 5.3A2 2 0 0 1 -9 9.3') : P('M-5.5 5.3A2 2 0 0 0 -5.5 9.3'));
const noX = (y: number) => Math.round((-4 * (11 - y)) / 7 * 100) / 100;
const ncX = (y: number) => Math.round((5.6 * (11 - y)) / 7.4 * 100) / 100;
/** Position switch S00259 / S00260: a small triangle on the blade itself (no link). */
const POS_NO = 'M-1.2 8.9L-3.8 8.3L-2.4 6.8';
const POS_NC = 'M1.96 8.41L1.24 6.19H3.64';
/** Float actuator S00195: a lens with a stub on top, at the end of the link. */
const FLOAT = '<ellipse cx="-10" cy="8" rx="2.5" ry="1.1"/>' + P('M-10 6.9V5.4');
/** Flow actuator S00197: a square, its top edge carried on toward the link. */
const FLOW = R(-12, 6, 4, 4) + P('M-8 6H-6.5');
/** Proximity actuator S00172: a rhombus with two lines in it, then a short solid line (S00359). */
const PROX = P('M-11.5 8L-9 5.5L-6.5 8L-9 10.5ZM-9.5 6.5V9.5M-8.5 6.5V9.5M-6.5 8H-5');
/** Fuse switch-disconnector pole S00370: the fuse is the blade, bar and circle on the fixed contact. */
const fsdPole = (x: number) => {
  const tip = [x - 5, 7.5], piv = [x, 16], dx = tip[0] - piv[0], dy = tip[1] - piv[1], l = Math.hypot(dx, dy);
  const u = [dx / l, dy / l], n = [-u[1], u[0]], m = [(tip[0] + piv[0]) / 2, (tip[1] + piv[1]) / 2];
  const c = (a: number, b: number) => `${(m[0] + u[0] * a + n[0] * b).toFixed(2)} ${(m[1] + u[1] * a + n[1] * b).toFixed(2)}`;
  return P(`M${x} 0V5.4M${x - 1.6} 5.4H${x + 1.6}M${x} 16V24M${x} 16L${x - 5} 7.5M${c(-3, -1)}L${c(3, -1)}L${c(3, 1)}L${c(-3, 1)}Z`) + C(x, 6.5, 1.1);
};
/** Measuring relay S00327: a box with the measured quantity in it (I>, θ> …), connected top and bottom. */
const mrel = (t: string) => P('M0 0V4M0 12V16') + R(-7, 4, 14, 8) + T(0, 9.3, 2.8, t);
const cbPole = (x: number) =>
  P(`M${x} 0V8M${x} 16V24M${x} 16L${x - 5} 7.5M${x - 1.1} 6.9l2.2 2.2m0 -2.2l-2.2 2.2`);
/** Thermal release: a box with the thermal effect step (S00120). */
const TRIP = R(-11, 9.5, 4, 5) + P('M-10.2 13.2h1v-2.4h1v2.4h1');
/** Electromagnetic (short-circuit) release: a box marked I>. */
const MAG = (x: number) => R(x, 9.5, 4, 5) + T(x + 2, 13, 2, 'I>');
/** Thermal-magnetic releases of a miniature or moulded case circuit breaker, side by side on the link. */
const TRIP_TM = TRIP + MAG(-15);
/** Contactor function S00218: a half circle on the end of the fixed contact, its flat side on the conductor. */
const kHalf = (x: number, y: number) => `M${x} ${y - 2.4}A1.2 1.2 0 0 0 ${x} ${y}`;
const POLES3: [number, number, string][] = [
  [0, 0, '1'], [8, 0, '3'], [16, 0, '5'], [0, 0, '2'], [8, 0, '4'], [16, 0, '6'],
];
const poles3 = (h: number): [number, number, string][] =>
  POLES3.map((p, i) => [p[0], i < 3 ? 0 : h, p[2]]);
const PASS3: [number, number][] = [[0, 3], [1, 4], [2, 5]];
/** switch-disconnector pole: isolating blade with the load-break circle on the fixed contact */
const lbsPole = (x: number) => P(`M${x} 0V5.4M${x - 1.6} 5.4H${x + 1.6}M${x} 16V24M${x} 16L${x - 5} 7.5`) + C(x, 6.5, 1.1);
/** residual current device: poles, the summation transformer drawn across them, trip unit */
const rcdSvg = (n: number) => {
  const xs = Array.from({ length: n }, (_, i) => i * 8), w = (n - 1) * 8;
  return xs.map((x) => P(`M${x} 0V8M${x} 16V24M${x} 16L${x - 4.5} 8.5`)).join('') + P(`M-3 12H${w + 3}`, 'dash') +
    `<ellipse cx="${w / 2}" cy="19.5" rx="${w / 2 + 4}" ry="2"/>` + R(-11, 9.5, 4, 5) + T(-9, 8, 2, 'IΔ');
};

/** Six-terminal motor for star-delta starting (U1 V1 W1 · W2 U2 V2 · PE). */
function motor6(): SymDef {
  const cx = 36, cy = 28, r = 12;
  const xs = [0, 8, 16, 48, 56, 64];
  let d = '';
  for (const x of xs) {
    const dx = x - cx, dy = 8 - cy, l = Math.hypot(dx, dy);
    d += `M${x} 0V8L${(cx + (dx / l) * r).toFixed(2)} ${(cy + (dy / l) * r).toFixed(2)}`;
  }
  d += `M72 0V${cy}H${cx + r}`;
  return {
    key: 'motor6', name: 'Asenkron motor 3~ (6 uç, Y/Δ)', cat: 'Güç devresi', prefix: 'M', role: 'main',
    pins: [[0, 0, 'U1'], [8, 0, 'V1'], [16, 0, 'W1'], [48, 0, 'W2'], [56, 0, 'U2'], [64, 0, 'V2'], [72, 0, 'PE']],
    bb: [-1, 0, 73, 40], svg: P(d) + C(cx, cy, r) + T(cx, cy + 2, 5.2, 'M') + T(cx, cy + 6, 2.4, '3~'),
  };
}

const defs: SymDef[] = [
  {
    // motor protection circuit breaker: breaker poles, a thermal element in each pole (adjustable overload) and the
    // short-circuit release on the link, so it reads differently from a miniature circuit breaker
    key: 'cb3', name: 'Motor koruma şalteri 3P', cat: 'Güç devresi', prefix: 'Q', role: 'main', mirror: true,
    pins: poles3(24), pass: PASS3, bb: [-11, 0, 18.5, 24],
    svg: p3((x) => P(`M${x} 0V8M${x} 16V17.5M${x} 22.5V24M${x} 16L${x - 5} 7.5M${x - 1.1} 6.9l2.2 2.2m0 -2.2l-2.2 2.2M${x} 17.5V19H${x + 1.2}V21H${x}V22.5`) + R(x - 1.8, 17.5, 3.6, 5)) +
      P('M-7 12H13.6', 'dash') + MAG(-11),
  },
  {
    key: 'k3', name: 'Kontaktör ana kontakları', cat: 'Güç devresi', prefix: 'K', role: 'aux', kind: 'no',
    pins: poles3(20), pass: PASS3, bb: [-4, 0, 17, 20],
    svg: p3((x) => P(`M${x} 0V6M${x} 14V20M${x} 14L${x - 4} 5${kHalf(x, 6)}`)) + P('M-2 9.5H14', 'dash'),
  },
  {
    // IEC 60617 S00154: mechanical interlock between two devices, a link with a triangle; drawn between the links
    // of a reversing contactor pair
    key: 'mint', name: 'Mekanik kilitleme', cat: 'Güç devresi', prefix: '', role: 'deco',
    pins: [], bb: [0, -0.5, 24, 2.6],
    svg: P('M0 0H10M14 0H24', 'dash') + P('M10 0H14L12 2.4Z'),
  },
  {
    // star contactor: the three poles are bridged into the star point inside the symbol
    // (no pass-through, so the star point never merges L1/L2/L3 in potential analysis)
    key: 'k3y', name: 'Yıldız kontaktörü (yıldız noktalı)', cat: 'Güç devresi', prefix: 'K', role: 'aux', kind: 'no',
    pins: [[0, 0, '1'], [8, 0, '3'], [16, 0, '5']], bb: [-4, 0, 17, 24],
    svg: p3((x) => P(`M${x} 0V6M${x} 14V20M${x} 14L${x - 4} 5${kHalf(x, 6)}`)) + P('M-2 9.5H14', 'dash') + P('M0 20H16M8 20V22'),
  },
  {
    key: 'ol3', name: 'Termik aşırı akım rölesi', cat: 'Güç devresi', prefix: 'F', role: 'main', mirror: true,
    pins: poles3(20), pass: PASS3, bb: [-2, 0, 18, 20],
    svg: p3((x) => R(x - 2, 6, 4, 8) + P(`M${x} 0V6M${x} 14V20M${x} 6V8.5H${x + 1.5}V11.5H${x}V14`)) + P('M2 10H6M10 10H14', 'dash'),
  },
  {
    key: 'motor3', name: 'Asenkron motor 3~', cat: 'Güç devresi', prefix: 'M', role: 'main',
    pins: [[0, 0, 'U1'], [8, 0, 'V1'], [16, 0, 'W1'], [24, 0, 'PE']], bb: [-1, 0, 25, 24],
    svg: P('M0 0L2.3 10.3M8 0V8M16 0L13.7 10.3M24 0V16H16') + C(8, 16, 8) + T(8, 18, 5.2, 'M') + T(8, 22, 2.4, '3~'),
  },
  motor6(),
  {
    key: 'vfd', name: 'Frekans konvertörü 3~', cat: 'Güç devresi', prefix: 'T', role: 'main',
    pins: [[0, 0, 'L1'], [8, 0, 'L2'], [16, 0, 'L3'], [32, 0, 'DI1'], [40, 0, 'COM'], [0, 32, 'U'], [8, 32, 'V'], [16, 32, 'W'], [24, 32, 'PE']],
    bb: [-4, 0, 44, 32],
    svg: P('M0 0V6M8 0V6M16 0V6M32 0V6M40 0V6M0 26V32M8 26V32M16 26V32M24 26V32M-4 26L28 6') + R(-4, 6, 48, 20) +
      T(2.5, 13, 3.2, '~') + T(20, 23, 3.2, '~') + T(36, 16.5, 2.4, 'DI'),
  },
  {
    // soft starter: the phases pass through (thyristors, bypassed at full speed); IN/COM is its start input
    key: 'ss', name: 'Soft starter 3~', cat: 'Güç devresi', prefix: 'T', role: 'main',
    pins: [[0, 0, '1L1'], [8, 0, '3L2'], [16, 0, '5L3'], [32, 0, 'IN'], [40, 0, 'COM'], [0, 32, '2T1'], [8, 32, '4T2'], [16, 32, '6T3']],
    pass: [[0, 5], [1, 6], [2, 7]],
    bb: [-4, 0, 44, 32],
    svg: P('M0 0V6M8 0V6M16 0V6M32 0V6M40 0V6M0 26V32M8 26V32M16 26V32') + R(-4, 6, 48, 20) + P('M2 22L12 11H22') + T(36, 16.5, 2.4, 'IN'),
  },
  {
    key: 'cb3n', name: 'Otomatik sigorta 3P', cat: 'Güç devresi', prefix: 'F', role: 'main', mirror: true,
    pins: poles3(24), pass: PASS3, bb: [-15, 0, 17, 24],
    svg: p3(cbPole) + P('M-7 12H13.6', 'dash') + TRIP_TM,
  },
  {
    // air circuit breaker with motor operator; its coils and charging motor are auxiliary functions of the same tag
    key: 'cb3m', name: 'Motorlu devre kesici 3P (ACB)', cat: 'Güç devresi', prefix: 'Q', role: 'main', mirror: true,
    pins: poles3(24), pass: PASS3, bb: [-21, 0, 17, 24],
    svg: p3(cbPole) + P('M-7 12H13.6', 'dash') + TRIP + P('M-14.5 12H-11', 'dash') + C(-17.5, 12, 3) + T(-17.5, 13.2, 3.2, 'M'),
  },
  {
    key: 'lbs3', name: 'Yük ayırıcı 3P (ana şalter)', cat: 'Güç devresi', prefix: 'Q', role: 'main', mirror: true,
    pins: poles3(24), pass: PASS3, bb: [-6, 0, 17, 24],
    svg: p3(lbsPole) + P('M-3.5 12H13.6', 'dash'),
  },
  {
    // IEC 60617 S00288: disconnector (isolator), the bar on the fixed contact; off-load only
    key: 'ds3', name: 'Ayırıcı 3P (yüksüz)', cat: 'Güç devresi', prefix: 'Q', role: 'main', mirror: true,
    pins: poles3(24), pass: PASS3, bb: [-6, 0, 17.6, 24],
    svg: p3((x) => P(`M${x} 0V8M${x - 1.6} 8H${x + 1.6}M${x} 16V24M${x} 16L${x - 5} 7.5`)) + P('M-3.5 12H13.6', 'dash'),
  },
  {
    // IEC 60617 S00370: fuse switch-disconnector (NH fuse rail switchable under load)
    key: 'fsd3', name: 'Sigortalı yük ayırıcı 3P', cat: 'Güç devresi', prefix: 'Q', role: 'main', mirror: true,
    pins: poles3(24), pass: PASS3, bb: [-7, 0, 17.6, 24],
    svg: p3(fsdPole) + P('M-3 12.6H13.6', 'dash'),
  },
  {
    // change-over switch I-0-II: per pole a two-way contact with centre-off (S00231) carrying the
    // switch-disconnector mark on both fixed contacts; drawn in position 0
    key: 'cos3', name: 'Komütatör şalter 1-0-2 3P', cat: 'Güç devresi', prefix: 'Q', role: 'main',
    pins: [[-4, 0, 'I.1'], [12, 0, 'I.3'], [28, 0, 'I.5'], [4, 0, 'II.1'], [20, 0, 'II.3'], [36, 0, 'II.5'], [0, 24, '2'], [16, 24, '4'], [32, 24, '6']],
    bb: [-6, 0, 38, 24],
    svg: [0, 16, 32].map((x) => P(`M${x - 4} 0V5.4M${x - 5.6} 5.4H${x - 2.4}M${x + 4} 0V5.4M${x + 2.4} 5.4H${x + 5.6}M${x} 18V9M${x} 20V24`) + C(x - 4, 6.5, 1.1) + C(x + 4, 6.5, 1.1) + C(x, 19, 1)).join('') +
      P('M-3 13H35', 'dash'),
  },
  {
    // IEC 60617 S00912: integrating instrument (energy meter), the band at the top
    key: 'kwh', name: 'Elektrik sayacı (kWh)', cat: 'Ölçü aletleri', prefix: 'P', role: 'main',
    pins: [[0, 0, '1'], [0, 20, '2']], bb: [-5, 0, 5, 20],
    svg: P('M0 0V3M0 17V20M-5 7H5') + R(-5, 3, 10, 14) + T(0, 13.4, 2.4, 'kWh'),
  },
  {
    // direct connected meters, terminals to DIN 43856: L 1 → 3, N 4 → 6 (single phase); L1 1 → 3, L2 4 → 6,
    // L3 7 → 9, N 10 → 12 (three phase, four wire)
    key: 'kwh1', name: 'Elektrik sayacı 1~ (L, N)', cat: 'Ölçü aletleri', prefix: 'P', role: 'main',
    pins: [[0, 0, '1'], [0, 20, '3'], [8, 0, '4'], [8, 20, '6']], pass: [[0, 1], [2, 3]], bb: [-3, 0, 11, 20],
    svg: P('M0 0V3M0 17V20M8 0V3M8 17V20M-3 7H11') + R(-3, 3, 14, 14) + T(4, 13.4, 2.4, 'kWh'),
  },
  {
    key: 'kwh3', name: 'Elektrik sayacı 3~ (L1, L2, L3, N)', cat: 'Ölçü aletleri', prefix: 'P', role: 'main',
    pins: [[0, 0, '1'], [0, 20, '3'], [8, 0, '4'], [8, 20, '6'], [16, 0, '7'], [16, 20, '9'], [24, 0, '10'], [24, 20, '12']],
    pass: [[0, 1], [2, 3], [4, 5], [6, 7]], bb: [-3, 0, 27, 20],
    svg: P('M0 0V3M0 17V20M8 0V3M8 17V20M16 0V3M16 17V20M24 0V3M24 17V20M-3 7H27') + R(-3, 3, 30, 14) + T(12, 13.4, 2.6, 'kWh'),
  },
  {
    // IEC 60617 S00327 measuring relays: overcurrent, earth fault, thermistor motor protection
    key: 'mrel_i', name: 'Aşırı akım rölesi (I>)', cat: 'Ölçü aletleri', prefix: 'F', role: 'main',
    pins: [[0, 0, 'S1'], [0, 16, 'S2']], bb: [-7, 0, 7, 16], svg: mrel('I>'),
  },
  {
    key: 'mrel_ie', name: 'Toprak kaçak rölesi (IE>)', cat: 'Ölçü aletleri', prefix: 'F', role: 'main',
    pins: [[0, 0, 'S1'], [0, 16, 'S2']], bb: [-7, 0, 7, 16], svg: mrel('IE>'),
  },
  {
    key: 'mrel_th', name: 'Termistör motor koruma rölesi (θ>)', cat: 'Ölçü aletleri', prefix: 'F', role: 'main',
    pins: [[0, 0, 'T1'], [0, 16, 'T2']], bb: [-7, 0, 7, 16], svg: mrel('θ>'),
  },
  {
    // core balance (toroidal) current transformer: the feeder's phases and N pass through it (IEC 60617 S00888 drawn
    // across the conductors), its secondary to an earth leakage relay
    key: 'cbct', name: 'Toroid akım trafosu (kaçak akım)', cat: 'Ölçü aletleri', prefix: 'T', role: 'main',
    pins: [[36, -4, 'S1'], [36, 4, 'S2']], bb: [-4, -4.5, 36, 4.5],
    svg: '<ellipse cx="12" cy="0" rx="15.5" ry="2.6"/>' + P('M26.4 -1.3L28.4 -4H36M26.4 1.3L28.4 4H36'),
  },
  {
    // earth leakage relay as wired: supply A1-A2 through the top and bottom, the toroid on S1-S2 below (S00327 with
    // IΔ>); its output contact (11-12/14) trips the breaker's shunt trip coil
    key: 'efr', name: 'Toprak kaçak rölesi (A1-A2, S1-S2)', cat: 'Ölçü aletleri', prefix: 'F', role: 'main',
    pins: [[0, 0, 'A1'], [0, 20, 'A2'], [8, 20, 'S1'], [16, 20, 'S2']], bb: [-4, 0, 20, 20],
    svg: P('M0 0V4M0 16V20M8 16V20M16 16V20') + R(-4, 4, 24, 12) + T(8, 11.5, 3, 'IΔ>'),
  },
  {
    // thermistor motor protection relay as wired: supply A1-A2 through the top and bottom, the motor's PTC sensors
    // on T1-T2 below (S00327 with θ>); its output contact (11-12/14) is drawn in the motor's control rung
    key: 'ptcr', name: 'Termistör motor koruma rölesi (A1-A2, T1-T2)', cat: 'Kumanda', prefix: 'F', role: 'main',
    pins: [[0, 0, 'A1'], [0, 20, 'A2'], [12, 20, 'T1'], [20, 20, 'T2']], bb: [-4, 0, 24, 20],
    svg: P('M0 0V4M0 16V20M12 16V20M20 16V20') + R(-4, 4, 28, 12) + T(10, 11.5, 3, 'θ>'),
  },
  {
    // residual current breaker with overcurrent protection (RCBO): breaker poles (×) through the summation
    // transformer, the residual current, magnetic and thermal releases on the link
    key: 'rcbo2', name: 'Kaçak akım korumalı otomatik sigorta 2P (RCBO)', cat: 'Güç devresi', prefix: 'F', role: 'main', mirror: true,
    pins: [[0, 0, '1'], [8, 0, 'N'], [0, 24, '2'], [8, 24, 'N']], pass: [[0, 2], [1, 3]], bb: [-19, 0, 12, 24],
    svg: [0, 8].map((x) => P(`M${x} 0V8M${x} 16V24M${x} 16L${x - 4.5} 8.5M${x - 1.1} 6.9l2.2 2.2m0 -2.2l-2.2 2.2`)).join('') +
      P('M-7 12H11', 'dash') + '<ellipse cx="4" cy="19.5" rx="8" ry="2"/>' + R(-11, 9.5, 4, 5) + T(-9, 8, 2, 'IΔ') + MAG(-19) + R(-15, 9.5, 4, 5) + P('M-14.2 13.2h1v-2.4h1v2.4h1'),
  },
  {
    key: 'mccb3', name: 'Kompakt şalter 3P (MCCB)', cat: 'Güç devresi', prefix: 'Q', role: 'main', mirror: true,
    pins: poles3(24), pass: PASS3, bb: [-15, 0, 21, 24],
    svg: p3(cbPole) + P('M-7 12H13.6', 'dash') + TRIP_TM + R(18, 9, 3, 6) + P('M18 12h3'),
  },
  {
    key: 'rcd2', name: 'Kaçak akım rölesi 2P', cat: 'Güç devresi', prefix: 'F', role: 'main', mirror: true,
    pins: [[0, 0, '1'], [8, 0, 'N'], [0, 24, '2'], [8, 24, 'N']], pass: [[0, 2], [1, 3]], bb: [-11, 0, 12, 24],
    svg: rcdSvg(2),
  },
  {
    key: 'rcd4', name: 'Kaçak akım rölesi 4P', cat: 'Güç devresi', prefix: 'F', role: 'main', mirror: true,
    pins: [[0, 0, '1'], [8, 0, '3'], [16, 0, '5'], [24, 0, 'N'], [0, 24, '2'], [8, 24, '4'], [16, 24, '6'], [24, 24, 'N']],
    pass: [[0, 4], [1, 5], [2, 6], [3, 7]], bb: [-11, 0, 28, 24],
    svg: rcdSvg(4),
  },
  {
    // surge protective device: L1 L2 L3 N each through a varistor to the common PE terminal
    key: 'spd4', name: 'Parafudr (aşırı gerilim koruma) 4P', cat: 'Güç devresi', prefix: 'F', role: 'main',
    pins: [[0, 0, 'L1'], [8, 0, 'L2'], [16, 0, 'L3'], [24, 0, 'N'], [12, 24, 'PE']], bb: [-3, 0, 27, 24],
    svg: [0, 8, 16, 24].map((x) => P(`M${x} 0V5M${x} 13V17M${x} 5V8`) + R(x - 1.5, 5, 3, 8) + P(`M${x - 1} 8H${x + 1}L${x} 10Z`, 'fill')).join('') + P('M0 17H24M12 17V24'),
  },
  {
    // energy analyser: current inputs from the three CTs (k/l) and voltage inputs L1 L2 L3 N, all at the bottom
    key: 'emeter', name: 'Enerji analizörü 3~', cat: 'Ölçü aletleri', prefix: 'P', role: 'main',
    pins: [[4, 32, 'S1.1'], [12, 32, 'S2.1'], [28, 32, 'S1.2'], [36, 32, 'S2.2'], [52, 32, 'S1.3'], [60, 32, 'S2.3'], [68, 32, 'V1'], [76, 32, 'V2'], [84, 32, 'V3'], [92, 32, 'VN']],
    bb: [0, 6, 96, 32],
    svg: R(0, 6, 96, 20) + P('M4 26V32M12 26V32M28 26V32M36 26V32M52 26V32M60 26V32M68 26V32M76 26V32M84 26V32M92 26V32') + T(48, 15, 3.2, 'kWh · kvarh · P') + T(48, 21.5, 2.4, 'I1   I2   I3   U'),
  },
  {
    // generator / mains controller (synchronising, load sharing, protection): its AC measuring inputs and supply;
    // its digital inputs and outputs are drawn as PLC channels with the same tag
    key: 'gctl', name: 'Jeneratör / şebeke kontrolörü', cat: 'Ölçü aletleri', prefix: 'A', role: 'main',
    pins: [[0, 0, 'I1'], [16, 0, 'I2'], [32, 0, 'I3'], [48, 0, 'IC'], [72, 0, 'UG1'], [88, 0, 'UG2'], [104, 0, 'UG3'], [120, 0, 'UGN'], [144, 0, 'UB1'], [160, 0, 'UB2'], [176, 0, 'UB3'], [192, 0, 'UBN'], [216, 0, '+'], [232, 0, '−'], [160, 36, 'CAN H'], [176, 36, 'CAN L'], [200, 36, 'RS485 A'], [216, 36, 'RS485 B']],
    bb: [-4, 0, 236, 36],
    svg: R(-4, 6, 240, 24) + P('M160 30V36M176 30V36M200 30V36M216 30V36') + P('M0 0V6M16 0V6M32 0V6M48 0V6M72 0V6M88 0V6M104 0V6M120 0V6M144 0V6M160 0V6M176 0V6M192 0V6M216 0V6M232 0V6') + T(116, 16, 3.2, 'G  ⇄  BUS') + T(24, 25, 2.2, 'I') + T(96, 25, 2.2, 'U gen') + T(168, 25, 2.2, 'U bara') + T(224, 25, 2.2, 'DC'),
  },
  {
    // three-phase voltage / phase failure relay: its contacts are drawn as contacts with the same tag
    key: 'vmr', name: 'Gerilim / faz koruma rölesi 3~', cat: 'Ölçü aletleri', prefix: 'K', role: 'main',
    pins: [[0, 0, 'L1'], [8, 0, 'L2'], [16, 0, 'L3'], [24, 0, 'N']],
    bb: [-4, 0, 28, 26],
    svg: P('M0 0V6M8 0V6M16 0V6M24 0V6') + R(-4, 6, 32, 20) + T(12, 15, 3, 'U <>') + T(12, 21.5, 2.2, '3~'),
  },
  {
    // operator panel on the door: 24 V supply, protective earth and the RS485 line to the controllers
    key: 'hmi', name: 'Operatör paneli (HMI)', cat: 'Ölçü aletleri', prefix: 'A', role: 'main',
    pins: [[0, 0, 'L+'], [8, 0, 'M'], [16, 0, 'PE'], [32, 0, 'A'], [40, 0, 'B']],
    bb: [-4, 0, 44, 30],
    svg: R(-4, 6, 48, 24) + P('M0 0V6M8 0V6M16 0V6M32 0V6M40 0V6') + R(4, 11, 32, 14) + T(20, 20, 3, 'HMI'),
  },
  {
    // three-phase power factor correction capacitor, delta connected, with built-in discharge resistors
    key: 'cap3', name: 'Kondansatör 3~ (kompanzasyon)', cat: 'Güç devresi', prefix: 'C', role: 'main',
    pins: [[0, 0, 'L1'], [8, 0, 'L2'], [16, 0, 'L3']], bb: [-3, 0, 19, 24],
    svg: P('M0 0V9M8 0V9M16 0V9M0 9H3M13 9H16M8 9V12') + P('M3 7V11M5 7V11M11 7V11M13 7V11M6.5 12H9.5M6.5 14H9.5M8 14V17M0 9V20H16V9M8 17V20') + T(8, 24, 2.2, 'kvar'),
  },
  {
    // reactive power controller: supply L/N and current input k/l on top; its step outputs are NO contacts with the same tag
    key: 'rpr', name: 'Reaktif güç rölesi', cat: 'Ölçü aletleri', prefix: 'K', role: 'main',
    pins: [[0, 0, 'L'], [8, 0, 'N'], [24, 0, 'k'], [32, 0, 'l']], bb: [-4, 0, 36, 26],
    svg: P('M0 0V6M8 0V6M24 0V6M32 0V6') + R(-4, 6, 40, 20) + T(16, 15, 3, 'cos φ') + T(16, 21.5, 2.2, 'var'),
  },
  {
    key: 'coil_cl', name: 'Kapama bobini (devre kesici)', cat: 'Güç devresi', prefix: 'Q', role: 'aux',
    pins: [[0, 0, 'A1'], [0, 20, 'A2']], bb: [-5, 0, 5, 20],
    // the general operating device S00305 (a diagonal would mark a remanent relay, S00323)
    svg: P('M0 0V6M0 14V20') + R(-5, 6, 10, 8),
  },
  {
    key: 'coil_uv', name: 'Düşük gerilim bobini (devre kesici)', cat: 'Güç devresi', prefix: 'Q', role: 'aux',
    pins: [[0, 0, 'D1'], [0, 20, 'D2']], bb: [-5, 0, 5, 20],
    svg: P('M0 0V6M0 14V20') + R(-5, 6, 10, 8) + T(0, 11.2, 2.6, 'Umin'),
  },
  {
    key: 'coil_st', name: 'Açtırma bobini (devre kesici)', cat: 'Güç devresi', prefix: 'Q', role: 'aux',
    pins: [[0, 0, 'C1'], [0, 20, 'C2']], bb: [-5, 0, 5, 20],
    svg: P('M0 0V6M0 14V20') + R(-5, 6, 10, 8) + T(0, 11.2, 3, 'ST'),
  },
  {
    key: 'mot_ch', name: 'Kurma motoru (devre kesici)', cat: 'Güç devresi', prefix: 'Q', role: 'aux',
    pins: [[0, 0, 'M1'], [0, 20, 'M2']], bb: [-5, 0, 5, 20],
    svg: P('M0 0V5M0 15V20') + C(0, 10, 5) + T(0, 11.4, 4, 'M'),
  },
  {
    key: 'k1', name: 'Kontaktör ana kontağı (1 kutup)', cat: 'Güç devresi', prefix: 'K', role: 'aux', kind: 'no',
    pins: [[0, 0, '1'], [0, 16, '2']], pass: [[0, 1]], bb: [-4, 0, 0.5, 16],
    svg: P(`M0 0V5M0 11V16M0 11L-4 4${kHalf(0, 5)}`),
  },
  {
    key: 'ol1', name: 'Termik röle (1 kutup)', cat: 'Güç devresi', prefix: 'F', role: 'main', mirror: true,
    pins: [[0, 0, '1'], [0, 16, '2']], pass: [[0, 1]], bb: [-3, 0, 3, 16],
    svg: P('M0 0V4M0 12V16M-1.6 10H-0.6V6H0.6V10H1.6') + R(-3, 4, 6, 8),
  },
  {
    key: 'gen3', name: 'Senkron jeneratör 3~', cat: 'Güç devresi', prefix: 'G', role: 'main',
    pins: [[0, 0, 'U'], [8, 0, 'V'], [16, 0, 'W'], [24, 0, 'N']], bb: [-1, 0, 25, 24],
    svg: P('M0 0L2.3 10.3M8 0V8M16 0L13.7 10.3M24 0V16H16') + C(8, 16, 8) + T(8, 18, 5.2, 'G') + T(8, 22, 2.4, '3~'),
  },
  {
    key: 'vt', name: 'Gerilim trafosu 1~ (kumanda trafosu)', cat: 'Güç devresi', prefix: 'T', role: 'main',
    pins: [[0, 0, '1'], [16, 0, '2'], [0, 24, '3'], [16, 24, '4']], bb: [-1, 0, 17, 24],
    svg: P('M0 0V8M16 0V8M0 8A2 2 0 0 0 4 8A2 2 0 0 0 8 8A2 2 0 0 0 12 8A2 2 0 0 0 16 8') +
      P('M0 12H16M0 13.2H16') + P('M0 17A2 2 0 0 1 4 17A2 2 0 0 1 8 17A2 2 0 0 1 12 17A2 2 0 0 1 16 17M0 17V24M16 17V24'),
  },
  {
    // primary is the conductor passing through; the secondary leads go to the meters
    key: 'ct', name: 'Akım trafosu', cat: 'Güç devresi', prefix: 'T', role: 'main',
    pins: [[0, 0, 'P1'], [0, 16, 'P2'], [8, 4, 'S1'], [8, 12, 'S2']], pass: [[0, 1]], bb: [-3.5, 0, 8, 16],
    svg: P('M0 0V16') + C(0, 8, 3) + P('M2.6 6.5L4.4 4H8M2.6 9.5L4.4 12H8'),
  },
  {
    key: 'rect', name: 'Redresör / akü şarj cihazı', cat: 'Güç devresi', prefix: 'T', role: 'main',
    pins: [[0, 0, 'L'], [8, 0, 'N'], [0, 24, '+'], [8, 24, '−']], bb: [-4, 0, 12, 24],
    svg: P('M0 0V6M8 0V6M0 18V24M8 18V24M-4 18L12 6') + R(-4, 6, 16, 12) + T(0.6, 11, 3.2, '~') + T(7.6, 16.4, 3.2, '='),
  },
  {
    key: 'battery', name: 'Akü', cat: 'Güç devresi', prefix: 'G', role: 'main',
    pins: [[0, 0, '+'], [0, 16, '−']], bb: [-5, 0, 5, 16],
    svg: P('M0 0V6.5M0 9.5V16M-5 6.5H5') + P('M-2.5 9H2.5V10.2H-2.5Z', 'fill') + T(3.8, 5, 2.4, '+'),
  },
  {
    key: 'heat', name: 'Isıtıcı', cat: 'Güç devresi', prefix: 'E', role: 'main',
    pins: [[0, 0, 'x1'], [0, 16, 'x2']], bb: [-2.5, 0, 2.5, 16],
    svg: P('M0 0V4M0 12V16') + R(-2.5, 4, 5, 8) + P('M-2.5 6H2.5M-2.5 8H2.5M-2.5 10H2.5'),
  },
  {
    key: 'diode', name: 'Diyot', cat: 'Güç devresi', prefix: 'V', role: 'main',
    pins: [[0, 0, 'A'], [0, 16, 'K']], pass: [[0, 1]], bb: [-3, 0, 3, 16],
    svg: P('M0 0V5M0 11V16M-3 5H3L0 11ZM-3 11H3'),
  },
  {
    key: 'cb1', name: 'Otomatik sigorta 1P', cat: 'Güç devresi', prefix: 'F', role: 'main', mirror: true,
    pins: [[0, 0, '1'], [0, 24, '2']], pass: [[0, 1]], bb: [-15, 0, 1.5, 24],
    svg: cbPole(0) + P('M-7 12H-2.4', 'dash') + TRIP_TM,
  },
  {
    key: 'fuse', name: 'Sigorta', cat: 'Güç devresi', prefix: 'F', role: 'main',
    pins: [[0, 0, '1'], [0, 16, '2']], pass: [[0, 1]], bb: [-2, 0, 2, 16],
    svg: R(-2, 3, 4, 10) + P('M0 0V16'),
  },
  {
    key: 'psu', name: 'Güç kaynağı AC/DC', cat: 'Güç devresi', prefix: 'G', role: 'main',
    pins: [[0, 0, 'L'], [8, 0, 'N'], [0, 24, '+'], [8, 24, '−']], bb: [-4, 0, 12, 24],
    svg: P('M0 0V6M8 0V6M0 18V24M8 18V24M-4 18L12 6') + R(-4, 6, 16, 12) + T(0.6, 11, 3.2, '~') + T(7.6, 16.4, 3.2, '='),
  },
  {
    key: 'coil', name: 'Kontaktör / röle bobini', cat: 'Kumanda', prefix: 'K', role: 'main', mirror: true,
    pins: [[0, 0, 'A1'], [0, 20, 'A2']], bb: [-5, 0, 5, 20],
    svg: P('M0 0V6M0 14V20') + R(-5, 6, 10, 8),
  },
  {
    key: 'no', name: 'Kapanan kontak (NO)', cat: 'Kumanda', prefix: 'K', role: 'aux', kind: 'no',
    pins: [[0, 0, ''], [0, 16, '']], pass: [[0, 1]], bb: [-4, 0, 0.5, 16], svg: P(NO),
  },
  {
    key: 'nc', name: 'Açan kontak (NC)', cat: 'Kumanda', prefix: 'K', role: 'aux', kind: 'nc',
    pins: [[0, 0, ''], [0, 16, '']], pass: [[0, 1]], bb: [-2, 0, 6, 16], svg: P(NC),
  },
  {
    // IEC 60617 S00243 / S00245: contacts of an on-delay timer, delayed when the relay is energised
    key: 'no_t', name: 'Gecikmeli kapanan kontak (zaman rölesi)', cat: 'Kumanda', prefix: 'K', role: 'aux', kind: 'no',
    pins: [[0, 0, ''], [0, 16, '']], pass: [[0, 1]], bb: [-8, 0, 0.5, 16], svg: P(NO) + delay(noX, false),
  },
  {
    key: 'nc_t', name: 'Gecikmeli açan kontak (zaman rölesi)', cat: 'Kumanda', prefix: 'K', role: 'aux', kind: 'nc',
    pins: [[0, 0, ''], [0, 16, '']], pass: [[0, 1]], bb: [-8, 0, 6, 16], svg: P(NC) + delay(ncX, false),
  },
  {
    // IEC 60617 S00244 / S00246: contacts of an off-delay timer, delayed when the relay is released
    key: 'no_toff', name: 'Düşmede gecikmeli kapanan kontak (zaman rölesi)', cat: 'Kumanda', prefix: 'K', role: 'aux', kind: 'no',
    pins: [[0, 0, ''], [0, 16, '']], pass: [[0, 1]], bb: [-9.5, 0, 0.5, 16], svg: P(NO) + delay(noX, true),
  },
  {
    key: 'nc_toff', name: 'Düşmede gecikmeli açan kontak (zaman rölesi)', cat: 'Kumanda', prefix: 'K', role: 'aux', kind: 'nc',
    pins: [[0, 0, ''], [0, 16, '']], pass: [[0, 1]], bb: [-9.5, 0, 6, 16], svg: P(NC) + delay(ncX, true),
  },
  {
    // IEC 60617 S00312 / S00311: time relay coils, on-delay (slow-operating, square with a cross) and off-delay
    // (slow-releasing, filled square)
    key: 'coil_on', name: 'Zaman rölesi bobini, çekmede gecikmeli', cat: 'Kumanda', prefix: 'K', role: 'main', mirror: true,
    pins: [[0, 0, 'A1'], [0, 20, 'A2']], bb: [-7, 0, 7, 20],
    svg: P('M0 0V6M0 14V20') + R(-7, 6, 14, 8) + R(-7, 6, 4, 8) + P('M-7 6L-3 14M-3 6L-7 14'),
  },
  {
    key: 'coil_off', name: 'Zaman rölesi bobini, düşmede gecikmeli', cat: 'Kumanda', prefix: 'K', role: 'main', mirror: true,
    pins: [[0, 0, 'A1'], [0, 20, 'A2']], bb: [-7, 0, 7, 20],
    svg: P('M0 0V6M0 14V20') + R(-7, 6, 14, 8) + P('M-7 6H-3V14H-7Z', 'fill'),
  },
  {
    key: 'pb_no', name: 'Buton, kapanan', cat: 'Kumanda', prefix: 'S', role: 'main',
    pins: [[0, 0, '13'], [0, 16, '14']], pass: [[0, 1]], bb: [-10.5, 0, 0.5, 16],
    svg: P(NO) + P('M-1.7 8H-9', 'dash') + P(PUSH),
  },
  {
    key: 'pb_nc', name: 'Buton, açan', cat: 'Kumanda', prefix: 'S', role: 'main',
    pins: [[0, 0, '11'], [0, 16, '12']], pass: [[0, 1]], bb: [-10.5, 0, 6, 16],
    svg: P(NC) + P('M2.3 8H-9', 'dash') + P(PUSH),
  },
  {
    // IEC 60617 S00258: mushroom actuator, detent (stays actuated), positive opening of the break contact
    key: 'estop', name: 'Acil stop', cat: 'Kumanda', prefix: 'S', role: 'main',
    pins: [[0, 0, '11'], [0, 16, '12']], pass: [[0, 1]], bb: [-12.5, 0, 6, 16],
    svg: P(NC) + P('M-9 8H-4.6M-2.4 8H2.3', 'dash') + P('M-4.6 8L-3.5 9.6L-2.4 8') + P('M-9 4.5V11.5M-9 4.5A3.5 3.5 0 0 0 -9 11.5') +
      C(-3.5, 3.2, 1.8) + P('M-4.7 3.2H-2.3M-3.1 2.4L-2.3 3.2L-3.1 4'),
  },
  {
    key: 'lamp', name: 'Sinyal lambası', cat: 'Kumanda', prefix: 'H', role: 'main',
    pins: [[0, 0, 'X1'], [0, 16, 'X2']], bb: [-4, 0, 4, 16],
    svg: P('M0 0V4M0 12V16M-2.83 5.17L2.83 10.83M2.83 5.17L-2.83 10.83') + C(0, 8, 4),
  },
  {
    // IEC 60617 S00966: flashing signal lamp, a pulse beside the lamp
    key: 'lamp_flash', name: 'Sinyal lambası, yanıp sönen', cat: 'Kumanda', prefix: 'H', role: 'main',
    pins: [[0, 0, 'X1'], [0, 16, 'X2']], bb: [-4, 0, 9, 16],
    svg: P('M0 0V4M0 12V16M-2.83 5.17L2.83 10.83M2.83 5.17L-2.83 10.83') + C(0, 8, 4) + P('M4.5 6.5H5.5V3.5H7.5V6.5H8.5'),
  },
  {
    key: 'co', name: 'Komütatör kontak (açan-kapanan)', cat: 'Kumanda', prefix: 'K', role: 'aux',
    pins: [[0, 16, ''], [-4, 0, ''], [0, 0, '']], pass: [[0, 1], [0, 2]], bb: [-5, 0, 0.5, 16],
    svg: P('M0 16V11L-3.4 4.4M0 0V5M-4 0V4.4H-2.9'),
  },
  {
    key: 'estop_no', name: 'Acil stop (kapanan kontak)', cat: 'Kumanda', prefix: 'S', role: 'main',
    pins: [[0, 0, '13'], [0, 16, '14']], pass: [[0, 1]], bb: [-12.5, 0, 0.5, 16],
    svg: P(NO) + P('M-9 8H-4.6M-2.4 8H-1.7', 'dash') + P('M-4.6 8L-3.5 9.6L-2.4 8') + P('M-9 4.5V11.5M-9 4.5A3.5 3.5 0 0 0 -9 11.5'),
  },
  {
    key: 'pb_latch', name: 'Mandallı buton (kalıcı)', cat: 'Kumanda', prefix: 'S', role: 'main',
    pins: [[0, 0, '13'], [0, 16, '14']], pass: [[0, 1]], bb: [-10.5, 0, 0.5, 16],
    svg: P(NO) + P('M-9 8H-6.2M-4.2 8H-1.7', 'dash') + P(PUSH) + P('M-6.2 8L-5.2 9.6L-4.2 8'),
  },
  {
    key: 'rot01', name: 'Pako şalter 0-1', cat: 'Kumanda', prefix: 'S', role: 'main',
    pins: [[0, 0, '1'], [0, 16, '2']], pass: [[0, 1]], bb: [-10, 0, 0.5, 16],
    svg: P(NO) + P('M-1.7 8H-8', 'dash') + P(TURN),
  },
  {
    // a further contact of a selector switch (the hand-off-auto selector's "auto" contact), same tag as the switch
    key: 'rot_no', name: 'Pako şalter kontağı (kapanan)', cat: 'Kumanda', prefix: 'S', role: 'aux', kind: 'no',
    pins: [[0, 0, ''], [0, 16, '']], pass: [[0, 1]], bb: [-10, 0, 0.5, 16],
    svg: P(NO) + P('M-1.7 8H-8', 'dash') + P(TURN),
  },
  {
    key: 'rot012', name: 'Pako şalter 0-1-2', cat: 'Kumanda', prefix: 'S', role: 'main',
    pins: [[0, 0, '1'], [0, 16, '2'], [8, 0, '3'], [8, 16, '4']], pass: [[0, 1], [2, 3]], bb: [-10, 0, 8.5, 16],
    svg: P(NO) + P('M8 0V5M8 11V16M8 11L4 4') + P('M-1.7 8H6.3', 'dash') + P('M-8 8H-1.7', 'dash') + P(TURN),
  },
  {
    // IEC 60617 S00259 / S00260: position switch, the triangle on the blade
    key: 'ls_no', name: 'Konum / kapı şalteri, kapanan', cat: 'Kumanda', prefix: 'S', role: 'main',
    pins: [[0, 0, '13'], [0, 16, '14']], pass: [[0, 1]], bb: [-6, 0, 2, 16],
    svg: P(NO) + P(POS_NO),
  },
  {
    key: 'ls_nc', name: 'Konum / kapı şalteri, açan', cat: 'Kumanda', prefix: 'S', role: 'main',
    pins: [[0, 0, '11'], [0, 16, '12']], pass: [[0, 1]], bb: [-6, 0, 6, 16],
    svg: P(NC) + P(POS_NC),
  },
  {
    // IEC 60617: float actuator S00195 on the link of a make / break contact (liquid level switch)
    key: 'fs_no', name: 'Şamandıralı seviye şalteri, kapanan', cat: 'Kumanda', prefix: 'B', role: 'main',
    pins: [[0, 0, '13'], [0, 16, '14']], pass: [[0, 1]], bb: [-13, 0, 0.5, 16],
    svg: P(NO) + P('M-7.5 8H-1.7', 'dash') + FLOAT,
  },
  {
    key: 'fs_nc', name: 'Şamandıralı seviye şalteri, açan', cat: 'Kumanda', prefix: 'B', role: 'main',
    pins: [[0, 0, '11'], [0, 16, '12']], pass: [[0, 1]], bb: [-13, 0, 6, 16],
    svg: P(NC) + P('M-7.5 8H2.3', 'dash') + FLOAT,
  },
  {
    // IEC 60617: fluid flow actuator S00197
    key: 'flow_no', name: 'Akış şalteri, kapanan', cat: 'Kumanda', prefix: 'B', role: 'main',
    pins: [[0, 0, '13'], [0, 16, '14']], pass: [[0, 1]], bb: [-12.5, 0, 0.5, 16],
    svg: P(NO) + P('M-8 8H-1.7', 'dash') + FLOW,
  },
  {
    key: 'flow_nc', name: 'Akış şalteri, açan', cat: 'Kumanda', prefix: 'B', role: 'main',
    pins: [[0, 0, '11'], [0, 16, '12']], pass: [[0, 1]], bb: [-12.5, 0, 6, 16],
    svg: P(NC) + P('M-8 8H2.3', 'dash') + FLOW,
  },
  {
    // pressure switch: IEC 60617 has no pressure actuator; drawn as is usual with "p" at the end of the link
    key: 'ps_no', name: 'Basınç şalteri, kapanan', cat: 'Kumanda', prefix: 'B', role: 'main',
    pins: [[0, 0, '13'], [0, 16, '14']], pass: [[0, 1]], bb: [-11.5, 0, 0.5, 16],
    svg: P(NO) + P('M-7.5 8H-1.7', 'dash') + T(-9.5, 9.1, 3.4, 'p'),
  },
  {
    key: 'ps_nc', name: 'Basınç şalteri, açan', cat: 'Kumanda', prefix: 'B', role: 'main',
    pins: [[0, 0, '11'], [0, 16, '12']], pass: [[0, 1]], bb: [-11.5, 0, 6, 16],
    svg: P(NC) + P('M-7.5 8H2.3', 'dash') + T(-9.5, 9.1, 3.4, 'p'),
  },
  {
    // IEC 60617 S00359: proximity switch, make contact
    key: 'prox_no', name: 'Yaklaşım şalteri, kapanan', cat: 'Kumanda', prefix: 'B', role: 'main',
    pins: [[0, 0, '13'], [0, 16, '14']], pass: [[0, 1]], bb: [-12, 0, 6, 16],
    svg: P(NO) + PROX + P('M-5 8H-1.7', 'dash'),
  },
  {
    key: 'prox_nc', name: 'Yaklaşım şalteri, açan', cat: 'Kumanda', prefix: 'B', role: 'main',
    pins: [[0, 0, '11'], [0, 16, '12']], pass: [[0, 1]], bb: [-12, 0, 6, 16],
    svg: P(NC) + PROX + P('M-5 8H2.3', 'dash'),
  },
  {
    // IEC 60617 S00361: proximity switch operated by iron (inductive sensor); here also as a make contact
    key: 'ind_no', name: 'Endüktif sensör, kapanan', cat: 'Kumanda', prefix: 'B', role: 'main',
    pins: [[0, 0, '13'], [0, 16, '14']], pass: [[0, 1]], bb: [-18, 0, 6, 16],
    svg: P(NO) + PROX + P('M-5 8H-1.7', 'dash') + T(-14.6, 9.1, 3, 'Fe'),
  },
  {
    key: 'ind_nc', name: 'Endüktif sensör, açan', cat: 'Kumanda', prefix: 'B', role: 'main',
    pins: [[0, 0, '11'], [0, 16, '12']], pass: [[0, 1]], bb: [-18, 0, 6, 16],
    svg: P(NC) + PROX + P('M-5 8H2.3', 'dash') + T(-14.6, 9.1, 3, 'Fe'),
  },
  {
    key: 'tstat', name: 'Termostat', cat: 'Kumanda', prefix: 'B', role: 'main',
    // IEC 60617 S00263: temperature sensitive switch, θ beside the contact (no link)
    pins: [[0, 0, '1'], [0, 16, '2']], pass: [[0, 1]], bb: [-10.5, 0, 5, 16],
    svg: P(NO) + T(2.8, 8.6, 3.4, 'θ'),
  },
  {
    // IEC 60617 S00264: temperature sensitive switch, break contact; θ to the right, beyond the blade
    key: 'tstat_nc', name: 'Termostat, açan', cat: 'Kumanda', prefix: 'B', role: 'main',
    pins: [[0, 0, '1'], [0, 16, '2']], pass: [[0, 1]], bb: [-10.5, 0, 7.5, 16],
    svg: P(NC) + T(5.2, 10.2, 3.4, 'θ'),
  },
  {
    key: 'lamp_led', name: 'Sinyal lambası LED (DC)', cat: 'Kumanda', prefix: 'H', role: 'main',
    pins: [[0, 0, 'x1'], [0, 16, 'x2']], bb: [-4, 0, 10, 16],
    svg: P('M0 0V4M0 12V16M-2.83 5.17L2.83 10.83M2.83 5.17L-2.83 10.83') + C(0, 8, 4) + T(7.2, 9, 2.2, 'LED'),
  },
  {
    key: 'lamp_tube', name: 'Aydınlatma lambası (floresan)', cat: 'Kumanda', prefix: 'E', role: 'main',
    pins: [[0, 0, 'x1'], [0, 20, 'x2']], bb: [-3.5, 0, 3.5, 20],
    svg: P('M0 0V2.5M0 17.5V20M-3 5.5A3 3 0 0 1 3 5.5V14.5A3 3 0 0 1 -3 14.5Z') + P('M-2 7L2 13M2 7L-2 13'),
  },
  {
    key: 'horn', name: 'Korna / siren', cat: 'Kumanda', prefix: 'H', role: 'main',
    pins: [[0, 0, 'x1'], [0, 16, 'x2']], bb: [-6, 0, 7.5, 16],
    svg: P('M0 0V6H-1.5M0 16V10H-1.5M-1.5 3.5V12.5M-1.5 3.5A4.5 4.5 0 0 0 -1.5 12.5'),
  },
  {
    // IEC 60617 S00973: buzzer (a bowl: the flat side with the leads), turned to the rung
    key: 'buzzer', name: 'Buzzer', cat: 'Kumanda', prefix: 'H', role: 'main',
    pins: [[0, 0, 'x1'], [0, 16, 'x2']], bb: [-2.5, 0, 6, 16],
    svg: P('M0 0V16M0 4.5H1.5M0 11.5H1.5M1.5 3.5V12.5M1.5 3.5A4.5 4.5 0 0 1 1.5 12.5'),
  },
  {
    key: 'res', name: 'Direnç', cat: 'Kumanda', prefix: 'R', role: 'main',
    pins: [[0, 0, '1'], [0, 16, '2']], bb: [-2, 0, 2, 16],
    svg: P('M0 0V4M0 12V16') + R(-2, 4, 4, 8),
  },
  {
    key: 'amp', name: 'Ampermetre', cat: 'Ölçü aletleri', prefix: 'P', role: 'main',
    pins: [[0, 0, 'x1'], [0, 16, 'x2']], bb: [-5, 0, 5, 16],
    svg: P('M0 0V3M0 13V16') + C(0, 8, 5) + T(0, 9.4, 4, 'A'),
  },
  {
    key: 'volt', name: 'Voltmetre', cat: 'Ölçü aletleri', prefix: 'P', role: 'main',
    pins: [[0, 0, 'x1'], [0, 16, 'x2']], bb: [-5, 0, 5, 16],
    svg: P('M0 0V3M0 13V16') + C(0, 8, 5) + T(0, 9.4, 4, 'V'),
  },
  {
    key: 'watt', name: 'Wattmetre', cat: 'Ölçü aletleri', prefix: 'P', role: 'main',
    pins: [[0, 0, 'x1'], [0, 16, 'x2']], bb: [-5, 0, 5, 16],
    svg: P('M0 0V3M0 13V16') + C(0, 8, 5) + T(0, 9.4, 4, 'W'),
  },
  {
    key: 'hz', name: 'Frekansmetre', cat: 'Ölçü aletleri', prefix: 'P', role: 'main',
    pins: [[0, 0, 'x1'], [0, 16, 'x2']], bb: [-5, 0, 5, 16],
    svg: P('M0 0V3M0 13V16') + C(0, 8, 5) + T(0, 9.2, 3, 'Hz'),
  },
  {
    // analog input of a frequency converter: a function of the drive (same tag), like a breaker's closing coil
    key: 'vfd_ai', name: 'Frekans konvertörü analog girişi (hız referansı)', cat: 'Güç devresi', prefix: 'T', role: 'aux',
    pins: [[0, 0, 'AI1'], [0, 16, 'GND']], bb: [-5, 0, 5, 16],
    svg: P('M0 0V3M0 13V16') + R(-5, 3, 10, 10) + T(0, 7.6, 2.2, 'AI') + T(0, 11, 2.2, 'n'),
  },
  {
    // dual-channel emergency stop relay: S11-S12 and S21-S22 e-stop channels, S33-S34 monitored reset;
    // its safety outputs (13-14, 23-24 …) are drawn as NO contacts with the same tag
    key: 'safrel', name: 'Güvenlik rölesi (acil stop)', cat: 'Kumanda', prefix: 'K', role: 'main',
    pins: [[0, 0, 'S11'], [8, 0, 'A1'], [16, 0, 'S21'], [24, 0, 'S33'], [32, 0, 'S34'], [0, 32, 'S12'], [8, 32, 'A2'], [16, 32, 'S22']],
    bb: [-4, 0, 36, 32],
    svg: P('M0 0V6M8 0V6M16 0V6M24 0V6M32 0V6M0 26V32M8 26V32M16 26V32') + R(-4, 6, 40, 20) + T(16, 18.2, 3.4, 'SAFETY'),
  },
  {
    // safe torque off input of a frequency converter: a function of the drive (same tag)
    key: 'vfd_sto', name: 'Frekans konvertörü STO girişi (güvenli tork kesme)', cat: 'Güç devresi', prefix: 'T', role: 'aux',
    pins: [[0, 0, 'STO'], [0, 16, 'GND']], bb: [-5, 0, 5, 16],
    svg: P('M0 0V3M0 13V16') + R(-5, 3, 10, 10) + T(0, 9.3, 2.4, 'STO'),
  },
  {
    key: 'vprop', name: 'Oransal vana 4-20 mA', cat: 'Kumanda', prefix: 'MB', role: 'main',
    pins: [[0, 0, '+'], [0, 16, '−']], bb: [-5, 0, 5, 16],
    svg: P('M0 0V3M0 13V16') + R(-5, 3, 10, 10) + P('M-5 13L5 3') + T(2.2, 11.4, 1.8, 'mA'),
  },
  {
    // 2-wire loop-powered transmitter: + from the 24 V supply, − to the analog input
    key: 'xmtr', name: 'Transmitter 4-20 mA (2 telli)', cat: 'Ölçü aletleri', prefix: 'B', role: 'main',
    pins: [[0, 0, '+'], [0, 16, '−']], bb: [-5, 0, 5, 16],
    svg: P('M0 0V3M0 13V16') + R(-5, 3, 10, 10) + T(0, 7.6, 2.2, '4-20') + T(0, 11, 2.2, 'mA'),
  },
  {
    key: 'plcin', name: 'PLC dijital giriş', cat: 'PLC', prefix: 'A', role: 'plc',
    pins: [[0, 0, '']], bb: [-8, 0, 8, 11], box: [0, 7.5], svg: P('M0 0V4') + R(-8, 4, 16, 7),
  },
  {
    key: 'plcout', name: 'PLC dijital çıkış', cat: 'PLC', prefix: 'A', role: 'plc',
    pins: [[0, 12, '']], bb: [-8, 1, 8, 12], box: [0, 4.5], svg: P('M0 8V12') + R(-8, 1, 16, 7),
  },
  {
    key: 'plcai', name: 'PLC analog giriş', cat: 'PLC', prefix: 'A', role: 'plc',
    pins: [[0, 0, '']], bb: [-8, 0, 8, 11], box: [0, 7.5], svg: P('M0 0V4') + R(-8, 4, 16, 7) + P('M-7 5.5l1.5 1.5l1.5 -1.5', ''),
  },
  {
    key: 'plcao', name: 'PLC analog çıkış', cat: 'PLC', prefix: 'A', role: 'plc',
    pins: [[0, 12, '']], bb: [-8, 1, 8, 12], box: [0, 4.5], svg: P('M0 8V12') + R(-8, 1, 16, 7) + P('M-7 5.5l1.5 -1.5l1.5 1.5', ''),
  },
  {
    key: 'term', name: 'Klemens', cat: 'Bağlantı', prefix: 'X', role: 'term',
    pins: [[0, 0, ''], [0, 8, '']], pass: [[0, 1]], bb: [-1.6, 0, 1.6, 8],
    svg: C(0, 4, 1.6) + P('M0 0V2.4M0 5.6V8'),
  },
  {
    key: 'pot', name: 'Potansiyel noktası', cat: 'Bağlantı', prefix: '', role: 'pot',
    pins: [[0, 0, '']], bb: [-4.5, -1.6, 0, 1.6], svg: P('M0 0H-2') + P('M-2 0L-4.5 -1.6V1.6Z', 'fill'),
  },
  {
    key: 'brk', name: 'Kesinti noktası', cat: 'Bağlantı', prefix: '', role: 'brk',
    pins: [[0, 0, '']], bb: [-2.2, 0, 2.2, 5], svg: P('M0 0V2.5M-2.2 2.5L0 5L2.2 2.5'),
  },
  {
    // connection definition point: placed on a wire, it gives that connection its cross-section, colour and number
    // (before the wire's own properties); a small filled rhombus, its values written beside it
    key: 'cdp', name: 'Bağlantı tanım noktası', cat: 'Bağlantı', prefix: '', role: 'deco',
    pins: [], bb: [-1.4, -1.4, 1.4, 1.4],
    svg: P('M0 -1.2L1.2 0L0 1.2L-1.2 0Z', 'fill'),
  },
  {
    // IEC 60617 S00032: male contact of a plug, the wire from below
    key: 'pin_m', name: 'Fiş pini (erkek)', cat: 'Bağlantı', prefix: 'X', role: 'term',
    pins: [[0, 8, '']], bb: [-1.8, 2, 1.8, 8],
    svg: P('M-0.7 2.4H0.7V3.6H-0.7Z', 'fill') + P('M0 3.6V8'),
  },
  {
    // IEC 60617 S00031: female contact of a socket, the wire from above
    key: 'pin_f', name: 'Soket pini (dişi)', cat: 'Bağlantı', prefix: 'X', role: 'term',
    pins: [[0, 0, '']], bb: [-1.8, 0, 1.8, 3.4],
    // the half circle turns its back to the wire and opens towards the mating male contact
    svg: P('M0 0V1.6M-1.8 3.4A1.8 1.8 0 0 1 1.8 3.4'),
  },
  {
    key: 'plug', name: 'Fiş / soket pini', cat: 'Bağlantı', prefix: 'X', role: 'term',
    pins: [[0, 0, ''], [0, 8, '']], pass: [[0, 1]], bb: [-1.8, 0, 1.8, 8],
    // IEC 60617 S00033: the socket's half circle opening downwards, the plug's male contact entering it from below
    svg: P('M0 0V1.6M-1.8 3.4A1.8 1.8 0 0 1 1.8 3.4M0 3.6V8') + P('M-0.7 2.4H0.7V3.6H-0.7Z', 'fill'),
  },
  {
    key: 'dblock', name: 'Dağıtım klemensi', cat: 'Bağlantı', prefix: 'X', role: 'term',
    pins: [[-8, 0, ''], [0, 0, ''], [8, 0, '']], pass: [[0, 1], [1, 2]], bb: [-8, -1.6, 8, 1.6],
    svg: P('M-8 0H-5.6M-2.4 0H2.4M5.6 0H8') + C(-4, 0, 1.6) + C(4, 0, 1.6),
  },
  {
    // an earth connection names its net PE (the name can be changed, e.g. GND)
    key: 'earth', name: 'Topraklama', cat: 'Bağlantı', prefix: '', role: 'pot',
    pins: [[0, 0, '']], bb: [-3, 0, 3, 6],
    svg: P('M0 0V3M-3 3H3M-2 4.5H2M-1 6H1'),
  },
  {
    // IEC 60617 S00202: protective earth, the earth sign in a circle
    key: 'earth_pe', name: 'Koruma topraklaması (PE)', cat: 'Bağlantı', prefix: '', role: 'pot',
    pins: [[0, 0, '']], bb: [-4.5, 0, 4.5, 9],
    svg: P('M0 0V3.4M-3 3.4H3M-2 4.9H2M-1 6.4H1') + C(0, 4.5, 4.5),
  },
  {
    key: 'socket', name: 'Pano içi priz', cat: 'Bağlantı', prefix: 'XS', role: 'main',
    pins: [[0, 0, 'L'], [8, 0, 'PE'], [16, 0, 'N']], bb: [-3, 0, 19, 10],
    svg: P('M0 0V5M16 0V5M8 0V3.5M5.5 3.5H10.5') + P('M-2.5 7.5A2.5 2.5 0 0 1 2.5 7.5M13.5 7.5A2.5 2.5 0 0 1 18.5 7.5') + P('M-3 2H19V10H-3Z', 'dash'),
  },
  {
    key: 'rj45', name: 'Ethernet bağlantısı (RJ45)', cat: 'Bağlantı', prefix: 'XF', role: 'main',
    pins: [[0, 0, '']], bb: [-4, 0, 4, 10],
    svg: P('M0 0V2') + R(-4, 2, 8, 8) + P('M-2.4 4H2.4V6.6H3.2V8.6H-3.2V6.6H-2.4Z', 'fill'),
  },
  // ---- single-line (one line stands for all phases) ----
  {
    key: 'sl_cb', name: 'Tek hat · şalter', cat: 'Tek hat', prefix: 'Q', role: 'sl',
    pins: [[0, 0, ''], [0, 16, '']], bb: [-5, 0, 2, 16],
    svg: P('M0 0V5M0 11V16M0 11L-4 4M-1.1 3.9l2.2 2.2m0 -2.2l-2.2 2.2'),
  },
  {
    key: 'sl_rcd', name: 'Tek hat · kaçak akım rölesi', cat: 'Tek hat', prefix: 'F', role: 'sl',
    pins: [[0, 0, ''], [0, 16, '']], bb: [-5, 0, 4, 16],
    svg: P('M0 0V5M0 11V16M0 11L-4 4') + '<ellipse cx="0" cy="13.5" rx="3.2" ry="1.3"/>' + T(-3.2, 3.2, 1.8, 'IΔ'),
  },
  {
    key: 'sl_k', name: 'Tek hat · kontaktör', cat: 'Tek hat', prefix: 'K', role: 'sl',
    pins: [[0, 0, ''], [0, 16, '']], bb: [-5, 0, 2, 16],
    svg: P(`M0 0V5M0 11V16M0 11L-4 4${kHalf(0, 5)}`),
  },
  {
    key: 'sl_ol', name: 'Tek hat · termik röle', cat: 'Tek hat', prefix: 'F', role: 'sl',
    pins: [[0, 0, ''], [0, 16, '']], bb: [-3, 0, 3, 16],
    svg: P('M0 0V4M0 12V16M-1.6 10H-0.6V6H0.6V10H1.6') + R(-3, 4, 6, 8),
  },
  {
    key: 'sl_fuse', name: 'Tek hat · sigorta', cat: 'Tek hat', prefix: 'F', role: 'sl',
    pins: [[0, 0, ''], [0, 16, '']], bb: [-2, 0, 2, 16],
    svg: P('M0 0V16') + R(-2, 3, 4, 10),
  },
  {
    key: 'sl_vfd', name: 'Tek hat · frekans konvertörü', cat: 'Tek hat', prefix: 'T', role: 'sl',
    pins: [[0, 0, ''], [0, 20, '']], bb: [-6, 0, 6, 20],
    svg: P('M0 0V4M0 16V20M-6 16L6 4') + R(-6, 4, 12, 12) + T(-2.6, 8.8, 2.8, '~') + T(2.8, 14.6, 2.8, '~'),
  },
  {
    key: 'sl_ss', name: 'Tek hat · soft starter', cat: 'Tek hat', prefix: 'T', role: 'sl',
    pins: [[0, 0, ''], [0, 20, '']], bb: [-6, 0, 6, 20],
    // IEC 60617 S00304: starter-regulator with thyristors (the starter square with its V, a thyristor, the
    // variability arrow)
    svg: P('M0 0V4M0 16V20M-6 4L0 6.5L6 4M-3.5 11.5H3.5M1.2 10V13M1.2 12.6L2.4 14.4') + R(-6, 4, 12, 12) + P('M-1.5 10V13L1.2 11.5Z') +
      P('M-4.5 14.8L4.2 6.6') + P('M4.6 6.2L2.6 6.8L4 8.2Z', 'fill'),
  },
  {
    // IEC 60617 S00297: motor starter, general (a square with a V from its top corners)
    key: 'sl_st', name: 'Tek hat · motor yol verici', cat: 'Tek hat', prefix: 'Q', role: 'sl',
    pins: [[0, 0, ''], [0, 20, '']], bb: [-6, 0, 6, 20],
    svg: P('M0 0V4M0 16V20M-6 4L0 6.5L6 4') + R(-6, 4, 12, 12),
  },
  {
    // IEC 60617 S00302: star-delta starter
    key: 'sl_yd', name: 'Tek hat · yıldız-üçgen yol verici', cat: 'Tek hat', prefix: 'Q', role: 'sl',
    pins: [[0, 0, ''], [0, 20, '']], bb: [-6, 0, 6, 20],
    svg: P('M0 0V4M0 16V20M-6 4L0 6.5L6 4M0 8.6L-2.6 13.2H2.6ZM0 11.7V8.6M0 11.7L-2.6 13.2M0 11.7L2.6 13.2') + R(-6, 4, 12, 12),
  },
  {
    // IEC 60617 S00288 / S00290 in single line: disconnector (bar) and switch-disconnector (bar and circle)
    key: 'sl_ds', name: 'Tek hat · ayırıcı', cat: 'Tek hat', prefix: 'Q', role: 'sl',
    pins: [[0, 0, ''], [0, 16, '']], bb: [-5, 0, 2, 16],
    svg: P('M0 0V5M-1.6 5H1.6M0 11V16M0 11L-4 4'),
  },
  {
    key: 'sl_lbs', name: 'Tek hat · yük ayırıcı', cat: 'Tek hat', prefix: 'Q', role: 'sl',
    pins: [[0, 0, ''], [0, 16, '']], bb: [-5, 0, 2, 16],
    svg: P('M0 0V3.4M-1.6 3.4H1.6M0 11V16M0 11L-4 4') + C(0, 4.5, 1.1),
  },
  {
    // fuse switch-disconnector in single line (IEC 60617 S00370): the fuse is the blade
    key: 'sl_fsd', name: 'Tek hat · sigortalı yük ayırıcı', cat: 'Tek hat', prefix: 'Q', role: 'sl',
    pins: [[0, 0, ''], [0, 24, '']], bb: [-7, 0, 2, 24],
    svg: fsdPole(0),
  },
  {
    // IEC 60617 S00912: energy meter in the line
    key: 'sl_kwh', name: 'Tek hat · elektrik sayacı', cat: 'Tek hat', prefix: 'P', role: 'sl',
    pins: [[0, 0, ''], [0, 20, '']], bb: [-5, 0, 5, 20],
    svg: P('M0 0V3M0 17V20M-5 7H5') + R(-5, 3, 10, 14) + T(0, 13.4, 2.4, 'kWh'),
  },
  {
    // IEC 60617 S00850: current transformer, the primary passing through; the secondary lead (two conductors)
    // runs to the right
    key: 'sl_ct', name: 'Tek hat · akım trafosu', cat: 'Tek hat', prefix: 'T', role: 'sl',
    pins: [[0, 0, ''], [0, 16, '']], bb: [-3, 0, 12, 16],
    svg: P('M0 0V5M0 11V16M3 8H12M7 9.6L8.4 6.4M8.4 9.6L9.8 6.4') + C(0, 8, 3),
  },
  {
    // IEC 60617 S00912: integrating instrument, the band at the top; here the energy analyser of the incomer
    key: 'sl_meter', name: 'Tek hat · enerji ölçümü', cat: 'Tek hat', prefix: 'P', role: 'sl',
    pins: [], bb: [0, -6, 14, 6],
    svg: R(0, -6, 14, 12) + P('M0 -3H14') + T(7, 2.6, 2.6, 'kWh'),
  },
  {
    // IEC 60617 S00373: surge diverter, between the line and earth
    key: 'sl_spd', name: 'Tek hat · parafudr', cat: 'Tek hat', prefix: 'F', role: 'sl',
    pins: [[0, 0, '']], bb: [-3, 0, 3, 22],
    svg: P('M0 0V5M0 15V19M-3 19H3M-2 20.5H2M-1 22H1') + R(-1.5, 5, 3, 10) + P('M-1 7H1L0 9Z', 'fill'),
  },
  {
    key: 'sl_m', name: 'Tek hat · motor', cat: 'Tek hat', prefix: 'M', role: 'sl',
    pins: [[0, 0, '']], bb: [-6, 0, 6, 16],
    svg: P('M0 0V4') + C(0, 10, 6) + T(0, 10.6, 4, 'M') + T(0, 14.2, 2.2, '3~'),
  },
  {
    key: 'sl_g', name: 'Tek hat · jeneratör', cat: 'Tek hat', prefix: 'G', role: 'sl',
    pins: [[0, 0, '']], bb: [-6, 0, 6, 16],
    svg: P('M0 0V4') + C(0, 10, 6) + T(0, 11.6, 5, 'G'),
  },
  {
    key: 'sl_tr', name: 'Tek hat · transformatör', cat: 'Tek hat', prefix: 'T', role: 'sl',
    pins: [[0, 0, ''], [0, 24, '']], bb: [-6, 0, 6, 24],
    svg: P('M0 0V3M0 21V24') + C(0, 9, 6) + C(0, 15, 6),
  },
  {
    key: 'sl_3p', name: 'Tek hat · 3 faz işareti', cat: 'Tek hat', prefix: '', role: 'sl',
    pins: [], bb: [-2.5, -2.5, 2.5, 2.5],
    svg: P('M-2.5 -0.5L2.5 -2.5M-2.5 1.5L2.5 -0.5M-2.5 3.5L2.5 1.5'),
  },
  {
    // size comes from props.w/h; the SVG here is only the library preview
    key: 'sbox', name: 'Yapı kutusu (=, +)', cat: 'Bağlantı', prefix: '', role: 'box',
    pins: [], bb: [0, 0, 24, 16], svg: P('M0 0H24V16H0Z', 'dash'),
  },
  {
    key: 'cable', name: 'Kablo tanımı', cat: 'Bağlantı', prefix: 'W', role: 'cable',
    pins: [], bb: [0, -1.5, 24, 1.5], svg: '',
  },
  {
    key: 'text', name: 'Serbest metin', cat: 'Bağlantı', prefix: '', role: 'text', pins: [], bb: [0, -3, 12, 1], svg: '',
  },
];

/** Structure box outline of a given size (mm). */
export const boxSvg = (w: number, h: number) => P(`M0 0H${w}V${h}H0Z`, 'dash');

/** Cable definition line of a given length (mm). */
export const cableSvg = (len: number, shield = false) =>
  P(`M0 -1.2V1.2M0 0H${len}M${len} -1.2V1.2`) +
  (shield ? P(`M${len * 0.12} 0A${len * 0.38} 2.4 0 0 1 ${len * 0.88} 0A${len * 0.38} 2.4 0 0 1 ${len * 0.12} 0`, 'dash') : '');
defs.find((d) => d.key === 'cable')!.svg = cableSvg(24);

defs.splice(defs.findIndex((d) => d.key === 'coil') + 1, 0, SOLENOID_DEF);
defs.push(...FLUID_DEFS, ...VENDOR_DEFS);
export const LIB: Record<string, SymDef> = Object.fromEntries(defs.map((d) => [d.key, d]));

/** The library symbol a symbol type stands for: itself, or for a user or imported symbol the one it was matched to. */
export const fnOf = (type: string) => LIB[type]?.fn ?? type;
export const LIB_ORDER = defs.map((d) => d.key);
/** Three-pole main contacts of a contactor: labelled with their own pin numbers, not counted as auxiliaries. */
export const isMainPoles = (type: string) => type === 'k3' || type === 'k3y' || type === 'k1';

export const PTYPES: [string, string][] = [
  ['', '—'], ['L', 'Faz (L)'], ['N', 'Nötr (N)'], ['PE', 'Toprak (PE)'], ['DC+', 'DC +'], ['DC-', 'DC −'],
];
/** Pins of plugs and sockets (terminal-like, listed in the plug plan, not in the terminal plan). */
export const PLUG_TYPES = new Set(['plug', 'pin_m', 'pin_f']);

export const WIRE_COLOURS: [string, string][] = [
  ['', '—'], ['BK', 'Siyah (BK)'], ['BN', 'Kahverengi (BN)'], ['GY', 'Gri (GY)'], ['BU', 'Mavi (BU)'],
  ['DBU', 'Koyu mavi (DBU)'], ['RD', 'Kırmızı (RD)'], ['WH', 'Beyaz (WH)'], ['OG', 'Turuncu (OG)'],
  ['GNYE', 'Yeşil-sarı (GNYE)'],
];
export const WIRE_SECTIONS = ['0.5', '0.75', '1', '1.5', '2.5', '4', '6', '10', '16', '25'];
